import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatLabel } from "@/lib/campus";
import { toast } from "sonner";
import { ArrowLeft, Lock, CheckCircle2, PlayCircle, Trophy, Mail, Repeat, Video, Music, Presentation } from "lucide-react";
import { MediaPlayer } from "@/components/campus/MediaPlayer";
import { LessonQuiz } from "@/components/campus/LessonQuiz";
import { TutorChat } from "@/components/campus/TutorChat";
import { RazorpayButton } from "@/components/campus/RazorpayButton";
import { LessonBlocks } from "@/components/campus/LessonBlocks";
import { ProfessorAvatar, PROFESSOR } from "@/components/campus/Professor";

const MEDIA_ICON = { video: Video, audio: Music, slides: Presentation };

const LessonReader = ({ lessons, active, setActive, capstone, unit }) => {
  const lesson = lessons[active];
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    document.querySelector("[data-testid=lesson-reader]")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [active]);
  if (!lesson) return <div className="rounded-2xl bg-[#121721] border border-white/10 p-6 text-slate-500">No {unit.toLowerCase()}s yet.</div>;
  return (
    <div className="rounded-2xl bg-[#121721] border border-white/10 p-6 scroll-mt-24" data-testid="lesson-reader">
      <div className="flex items-center gap-2 text-amber-400 text-sm font-semibold mb-1"><PlayCircle className="h-4 w-4" /> {unit} {active + 1} of {lessons.length}</div>
      <h2 className="text-xl font-bold text-white" data-testid="lesson-title">{lesson.title}</h2>
      {lesson.duration && <div className="text-xs text-slate-500 mt-1">{lesson.duration}</div>}
      {lesson.media?.length > 0 && <div className="mt-5 space-y-4">{lesson.media.map((m, i) => <MediaPlayer key={m.path} item={m} index={i} />)}</div>}
      {lesson.blocks?.length ? <LessonBlocks key={`blocks-${lesson.id}`} blocks={lesson.blocks} /> : <div className="mt-5 text-slate-300 text-[15px] leading-relaxed whitespace-pre-line">{lesson.body || lesson.content}</div>}
      <LessonQuiz key={`quiz-${lesson.id}`} quiz={lesson.quiz} />
      <div className="flex justify-between mt-8 pt-5 border-t border-white/10">
        <button data-testid="lesson-prev" disabled={active === 0} onClick={() => setActive(active - 1)} className="text-sm text-slate-400 hover:text-white disabled:opacity-30">← Previous</button>
        <button data-testid="lesson-next" disabled={active >= lessons.length - 1} onClick={() => setActive(active + 1)} className="text-sm text-amber-400 hover:text-amber-300 disabled:opacity-30">Next →</button>
      </div>
      {capstone && active >= lessons.length - 1 && (
        <div className="mt-6 rounded-xl bg-amber-500/10 border border-amber-500/30 p-5">
          <div className="flex items-center gap-2 text-amber-300 font-bold mb-1"><Trophy className="h-4 w-4" /> Your capstone</div>
          <p className="text-sm text-amber-200/90">{capstone}</p>
        </div>
      )}
    </div>
  );
};

export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [access, setAccess] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [capstone, setCapstone] = useState("");
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);

  const loadLessons = () => api.get(`/courses/${courseId}/lessons`).then(({ data }) => {
    setLessons(data.lessons || []); setCapstone(data.capstone || ""); setAccess(true);
  }).catch(() => setAccess(false));

  useEffect(() => { api.get(`/courses/${courseId}`).then(({ data }) => setCourse(data)).catch(() => toast.error("Course not found")); }, [courseId]);
  useEffect(() => { if (user) loadLessons(); else setAccess(false); }, [user, courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const enroll = async () => {
    if (!user) { toast.error("Sign in to enroll"); navigate("/login"); return; }
    setBusy(true);
    try {
      if (course.free) {
        await api.post(`/courses/${courseId}/enroll`);
        toast.success("Enrolled — your lessons are unlocked");
        await loadLessons();
      } else {
        const { data } = await api.post(`/courses/${courseId}/checkout`, { origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not enroll");
    } finally {
      setBusy(false);
    }
  };

  if (!course) return <div className="min-h-screen bg-[#0B0E14]"><Navbar /></div>;
  const unit = course.format === "series" ? "Episode" : "Lesson";
  const isOwner = user && course.instructor_id === user.user_id;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-slate-100">
      <Navbar />
      <div className="max-w-6xl mx-auto p-6 md:p-10">
        <Link to="/campus" data-testid="course-back" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-amber-400 mb-6"><ArrowLeft className="h-4 w-4" /> Back to Campus</Link>
        {course.cover && (
          <div className="relative rounded-2xl overflow-hidden border border-white/10 mb-6 aspect-[21/9] max-h-[340px] w-full" data-testid="course-cover">
            <img src={course.cover} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/30 to-transparent" />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {course.level && <span className={`text-xs font-bold px-3 py-1 rounded-full border ${{ Beginner: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", Intermediate: "bg-sky-500/10 text-sky-300 border-sky-500/30" }[course.level] || "bg-violet-500/10 text-violet-300 border-violet-500/30"}`} data-testid="course-level">{course.level}</span>}
          {course.ai_taught && <span data-testid="course-ai-taught" title="Only classes about AI are taught by AI on Hustler's Campus" className="text-xs font-bold px-3 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">AI-taught by Professor Sparks</span>}
          <span className="text-xs font-semibold px-3 py-1 rounded-full bg-white/5 text-slate-300 border border-white/10" data-testid="course-format">{formatLabel(course.format)}</span>
          <span className="text-xs text-slate-500">{course.category}</span>
          {course.ongoing && <span className="text-xs text-amber-300 flex items-center gap-1"><Repeat className="h-3 w-3" /> New episodes added over time</span>}
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight">{course.title}</h1>
        <p className="text-slate-400 mt-3 max-w-3xl">{course.description}</p>
        {course.recommended_for && <p className="mt-3 text-sm font-semibold text-amber-300" data-testid="course-recommended">{course.recommended_for}</p>}
        <div className="text-xs text-slate-500 mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="flex items-center gap-2">{course.instructor_name === PROFESSOR.name && <ProfessorAvatar size={28} />}By <strong className="text-slate-300">{course.instructor_name}</strong></span>
          <span>⭐ {course.rating} ({course.students})</span>
          <span>{course.lesson_count} {unit.toLowerCase()}s</span>
          {course.contact_email && (
            <a href={`mailto:${course.contact_email}`} data-testid="course-contact" className="flex items-center gap-1 text-amber-400 hover:text-amber-300"><Mail className="h-3 w-3" /> Email the instructor</a>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          <div className="lg:col-span-2 order-2 lg:order-1">
            {access ? (
              <>
                <LessonReader lessons={lessons} active={active} setActive={setActive} capstone={capstone} unit={unit} />
                <TutorChat courseId={courseId} />
              </>
            ) : (
              <div className="rounded-2xl bg-[#121721] border border-white/10 p-6">
                {course.outcomes?.length > 0 && (
                  <div className="mb-6" data-testid="course-outcomes">
                    <h3 className="text-lg font-bold text-white mb-3">What you'll be able to do</h3>
                    <ul className="grid sm:grid-cols-2 gap-2">
                      {course.outcomes.map((o) => <li key={o} className="flex gap-2 text-sm text-slate-300"><CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />{o}</li>)}
                    </ul>
                  </div>
                )}
                <h3 className="text-lg font-bold text-white mb-4">What's inside</h3>
                <ul className="space-y-3">
                  {(course.curriculum || []).map((l) => (
                    <li key={l.id} className="flex items-center gap-3 text-sm text-slate-400">
                      <Lock className="h-4 w-4 text-slate-600 shrink-0" />
                      <span className="text-slate-300">{l.title}</span>
                      <span className="ml-auto flex items-center gap-1.5">
                        {(l.media_kinds || []).map((k) => { const I = MEDIA_ICON[k]; return I ? <I key={k} className="h-3.5 w-3.5 text-slate-500" /> : null; })}
                        {l.duration && <span className="text-xs text-slate-600">{l.duration}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="order-1 lg:order-2">
            <div className="rounded-2xl bg-[#121721] border border-white/10 p-6 lg:sticky lg:top-24">
              <div className="text-3xl font-extrabold text-amber-400 mb-4">{course.free ? "Free" : `$${Number(course.price).toFixed(2)}`}</div>
              {access ? (
                <>
                  <div className="flex items-center gap-2 text-amber-400 text-sm font-semibold mb-4"><CheckCircle2 className="h-4 w-4" /> {isOwner ? "Your course" : "You're enrolled"}</div>
                  <div className="space-y-1">
                    {lessons.map((l, i) => (
                      <button key={l.id} data-testid={`lesson-nav-${i}`} onClick={() => setActive(i)} className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${i === active ? "bg-amber-500/10 text-amber-300" : "text-slate-400 hover:bg-white/5"}`}>
                        <span className="text-slate-600 mr-2">{i + 1}.</span>{l.title}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <button data-testid="course-enroll-btn" onClick={enroll} disabled={busy} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold py-3 rounded-xl transition-colors disabled:opacity-50">
                    {busy ? "…" : course.free ? "Enroll Free & Start" : `Enroll — $${Number(course.price).toFixed(2)}`}
                  </button>
                  {!course.free && user && <RazorpayButton courseId={courseId} onPaid={loadLessons} />}
                </>
              )}
              <p className="text-xs text-slate-500 mt-4">{course.free ? "Official Hustler's Campus course — free." : course.is_platform ? "Official Hustler's Campus course. Secure checkout." : `Secure checkout. The creator keeps 93%.${course.ongoing ? " One payment unlocks every future episode." : ""}`}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
